import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Search, ChevronRight, ChevronDown, RefreshCw, Package } from 'lucide-react';

import { STATUS_COLORS } from '@/lib/status-colors';
import { Screen, Pane, CommandBar, StatStrip, DataGrid } from '@/components/data';

export default function SupervisorOrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchDebounce, setSearchDebounce] = useState(search);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const limit = 25;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      if (searchDebounce) params.set('search', searchDebounce);
      if (statusFilter) params.set('status', statusFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      
      const res = await api.get(`/orders?${params.toString()}`);
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load orders');
    }
    setLoading(false);
  }, [page, searchDebounce, statusFilter, priorityFilter]);

  useEffect(() => { setPage(1); }, [searchDebounce, statusFilter, priorityFilter]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const totalPages = Math.ceil(total / limit);

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Orders with their job cards folded in underneath — a supervisor reads an
  // order to find the card that is stuck, so the cards belong in the same grid.
  const rows = [];
  for (const order of orders) {
    rows.push({ ...order, __kind: 'order', __rowId: order.id });
    if (expandedOrder === order.id) {
      for (const item of order.line_items || []) {
        rows.push({ ...item, __kind: 'card', __depth: 1, __order: order, __rowId: `${order.id}:${item.id}` });
      }
    }
  }

  const columns = [
    {
      key: 'name',
      header: 'Item / Customer',
      width: 'minmax(13rem,1.5fr)',
      render: (row) => {
        if (row.__kind === 'card') {
          return (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-slate-700">
                {row.item_name || row.product_subtype || 'Item'}
              </span>
              {row.current_stage && (
                <span className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700">
                  {row.current_stage}
                </span>
              )}
            </span>
          );
        }
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleExpand(row.id); }}
              aria-label={expandedOrder === row.id ? 'Collapse job cards' : 'Expand job cards'}
              className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            >
              {expandedOrder === row.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            <span className="min-w-0">
              <span className="block truncate font-semibold text-slate-900">
                {row.line_items?.[0]?.item_name || row.customer_name}
              </span>
              <span className="block truncate text-[11px] leading-tight text-slate-500">
                {row.customer_name}{row.firm_name ? ` — ${row.firm_name}` : ''}
              </span>
            </span>
            <span className="shrink-0 text-[10px] text-slate-400">{row.line_items?.length || 0} cards</span>
          </span>
        );
      },
    },
    {
      key: 'order_id',
      header: 'Order',
      width: '9.5rem',
      mobile: false,
      render: (row) => row.__kind === 'card'
        ? <span className="text-[11px] text-slate-400">Qty {row.quantity?.toLocaleString()}</span>
        : <span className="block truncate text-[11px] text-slate-400">{row.order_id}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '9rem',
      render: (row) => row.__kind === 'card' ? (
        <span className="text-[11px] text-slate-500">{row.status?.replace('_', ' ') || '—'}</span>
      ) : (
        <Badge className={`text-[10px] ${STATUS_COLORS[row.status]?.class || ''}`}>
          {row.location?.label || STATUS_COLORS[row.status]?.label || row.status}
        </Badge>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      mobile: false,
      render: (row) => row.__kind === 'card' ? null : (
        <span className={`text-[11px] font-medium ${
          row.priority === 'high' ? 'text-red-600' : row.priority === 'low' ? 'text-emerald-600' : 'text-amber-600'
        }`}>{row.priority}</span>
      ),
    },
    {
      key: 'expected_delivery_date',
      header: 'Delivery',
      width: '6.5rem',
      mobile: false,
      render: (row) => row.__kind === 'card' ? null : (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {row.expected_delivery_date ? formatIST(row.expected_delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '4rem',
      align: 'right',
      render: (row) => (
        <span className="flex items-center justify-end">
          <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        </span>
      ),
    },
  ];

  return (
    <Screen data-testid="supervisor-orders-page">
      <CommandBar
        title="Orders"
        subtitle={`${total} order${total === 1 ? '' : 's'}`}
        actions={[{ key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchOrders, disabled: loading }]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-11 pl-8 text-base lg:h-7 lg:text-xs"
            placeholder="Order, customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[{ key: null, label: 'orders', value: total, tone: 'blue' }]}
      >
        {/* seven buttons and two labels; on a phone they take two lines rather than a scroll bar */}
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-400">Status</span>
          {[['design', 'Design'], ['in_production', 'Production'], ['ready_for_dispatch', 'Ready']].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setStatusFilter(statusFilter === value ? '' : value)}
              aria-pressed={statusFilter === value}
              className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                statusFilter === value
                  ? 'bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30'
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {label}
            </button>
          ))}
          <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">Priority</span>
          {['high', 'medium', 'low'].map(level => (
            <button
              key={level}
              type="button"
              onClick={() => setPriorityFilter(priorityFilter === level ? '' : level)}
              aria-pressed={priorityFilter === level}
              className={`rounded px-2 py-1 text-[11px] font-medium capitalize transition-colors ${
                priorityFilter === level
                  ? 'bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30'
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {level}
            </button>
          ))}
        </span>
      </StatStrip>

      <Pane
        title="Orders"
        aside={`${orders.length} shown`}
        scroll={false}
        bodyClassName="flex flex-col"
        actions={totalPages > 1 && (
          <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
            {page} / {totalPages}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
          </span>
        )}
      >
        <DataGrid
          data-testid="supervisor-orders-table"
          columns={columns}
          rows={rows}
          loading={loading}
          getRowId={(row) => row.__rowId}
          onRowClick={(row) => navigate(
            row.__kind === 'card'
              ? `/supervisor/job-card/${row.id}`
              : `/supervisor/orders/${row.id}`
          )}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Package className="h-8 w-8 text-slate-300" />
              No orders match these filters
            </span>
          }
        />
      </Pane>
    </Screen>
  );
}
